import React, { useCallback, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { getPosLocationCurrency } from "../common/helpers/function";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { mergeCashRegisterExpectedActualModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_EXPECTED = "#5C74C7";
const COLOR_ACTUAL = "#5CC7C1";

function formatKTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}

/**
 * Cash Register Expected VS Actual — chart-only card.
 * Kebab menu: Download / Remove (no Chart/Table toggles).
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CashRegisterExpectedActualModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function CashRegisterExpectedActualChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const currencyCode = getPosLocationCurrency();

  const merged = useMemo(
    () => mergeCashRegisterExpectedActualModel(model && typeof model === "object" ? model : null),
    [model],
  );

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("cash-register-expected-actual"));
  }, []);

  const { chartData, options } = useMemo(() => {
    const hasData = (merged.labels?.length ?? 0) > 0;
    const labels = hasData
      ? merged.labels
      : [t("No data", { defaultValue: "No data" })];
    const expected = hasData ? merged.expected : [0];
    const actual = hasData ? merged.actual : [0];
    const n = labels.length;

    const maxY = Math.max(10000, ...expected, ...actual, 0);
    const yTop = Math.max(10000, Math.ceil(maxY / 10000) * 10000);

    const dsExpected = t("Expected", { defaultValue: "Expected" });
    const dsActual = t("Actual", { defaultValue: "Actual" });

    return {
      chartData: {
        labels,
        datasets: [
          {
            type: "line",
            label: dsExpected,
            data: expected,
            borderColor: COLOR_EXPECTED,
            backgroundColor: COLOR_EXPECTED,
            showLine: false,
            pointStyle: "line",
            pointRadius: 8,
            pointHoverRadius: 8,
            borderWidth: 2,
            tension: 0,
            yAxisID: "y",
            order: 0,
          },
          {
            type: "bar",
            label: dsActual,
            data: actual,
            backgroundColor: COLOR_ACTUAL,
            maxBarThickness: 30,
            borderRadius: 0,
            borderSkipped: false,
            yAxisID: "y",
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
              color: "#2A2A2A",
              font: { family: "'Segoe UI', Roboto, sans-serif", size: 12 },
              generateLabels: (chart) => {
                const ds = chart?.data?.datasets ?? [];
                return ds.map((d, i) => ({
                  text: String(d?.label ?? ""),
                  fillStyle: d?.backgroundColor ?? "#5CC7C1",
                  strokeStyle: d?.borderColor ?? d?.backgroundColor ?? "#5CC7C1",
                  lineWidth: d?.label === dsExpected ? 2 : 0,
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                  pointStyle: d?.label === dsExpected ? "line" : "circle",
                }));
              },
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const v = items?.[0]?.label;
                return v ? [String(v)] : [];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "#757575",
              font: { family: "'Segoe UI', Roboto, sans-serif", size: 10 },
              maxRotation: n > 7 ? 35 : 0,
              minRotation: 0,
            },
            border: { display: false },
          },
          y: {
            min: 0,
            max: yTop,
            grid: { color: "#E0E0E0", drawTicks: false },
            border: { display: false },
            title: {
              display: true,
              text: t("dashboardCurrencyThousandsAxis", {
                currency: currencyCode,
                defaultValue: "{{currency}} (thousands)",
              }),
              color: "#373D3F",
              font: { family: "'Segoe UI', Roboto, sans-serif", size: 11 },
            },
            ticks: {
              stepSize: Math.max(10000, Math.ceil(yTop / 6 / 10000) * 10000),
              color: "#373D3F",
              font: { family: "'Segoe UI', Roboto, sans-serif", size: 11 },
              callback: (v) => formatKTick(v),
            },
          },
        },
      },
    };
  }, [merged, t, currencyCode]);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--cash-register-expected-actual">
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">
          {t("Cash Register Expected VS Actual", { defaultValue: "Cash Register Expected VS Actual" })}
        </h2>
        <DashboardChartCardKebabMenu
          showViewToggle={false}
          onDownload={handleDownloadPng}
          onRemove={onMenuRemove}
        />
      </header>
      <div className="pos-dashboard-chart-card__body">
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          <div className="pos-dashboard-chart-card__canvas">
            <Chart type="bar" data={chartData} options={options} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CashRegisterExpectedActualChartCard;
